import { LoginService } from './LoginService';
import Api from 'services/PtmApi';
import { Content, ContentCode, ContentCodeActivation, ContentCodeEditData } from 'models/Content';
import { PTProgram } from '../models/PTProgram';
import { ApiError } from 'models/ApiError';

export class ContentService {
	private loginService: LoginService;

	constructor(loginService: LoginService) {
		this.loginService = loginService;
	}

	public getContent(): Promise<Array<Content>> {
		const session = this.loginService.getSession();

		if (session === undefined)
			return Promise.reject("No session");

		if (typeof session.accessToken !== 'string')
			return Promise.reject(ApiError.InvalidAccessToken);

		let url = Api.getBaseUrl() + 'v1/content';

		let init = {
			method: 'GET',
			headers: {
				'Authorization': 'Bearer ' + session.accessToken
			}
		};

		return fetch(url, init).then((response: Response) => {
			if (response.status === 200)
				return response.json();
			else
				throw new Error("Request failed, status: " + response.status);
		}).then((jsonObject: any) => {
			console.log(jsonObject);

			if (typeof jsonObject === 'object' &&
				Array.isArray(jsonObject['items'])) {
				let contents = new Array<Content>();

				for (let item of jsonObject['items']) {
					let content = Content.fromJson(item);

					if (content !== undefined)
						contents.push(content);
				}

				return contents;
			}
			else {
				throw new Error("Parsing content data failed");
			}
		}).catch(reason => Promise.reject(reason));
	}

	public async createContent(data: Content): Promise<number> {
		const session = this.loginService.getSession();

		if (session === undefined)
			return Promise.reject("No session");

		if (typeof session.accessToken !== 'string')
			return Promise.reject(ApiError.InvalidAccessToken);

		let url = Api.getBaseUrl() + 'v1/content';

		let init = {
			method: 'POST',
			headers: {
				'Authorization': 'Bearer ' + session.accessToken
			},
			body: JSON.stringify(data)
		};

		let result = await fetch(url, init);

		return result.status;
	}

	public async editContent(data: Content): Promise<number> {
		const session = this.loginService.getSession();

		if (session === undefined)
			return Promise.reject("No session");

		if (typeof session.accessToken !== 'string')
			return Promise.reject(ApiError.InvalidAccessToken);

		let url = Api.getBaseUrl() + 'v1/content';

		let init = {
			method: 'PUT',
			headers: {
				'Authorization': 'Bearer ' + session.accessToken
			},
			body: JSON.stringify(data)
		};

		let result = await fetch(url, init);

		return result.status;
	}

	public async getContentCodes(contentId: string): Promise<ContentCode[]> {
		const session = this.loginService.getSession();

		if (session === undefined)
			return Promise.reject("No session");

		if (typeof session.accessToken !== 'string')
			return Promise.reject(ApiError.InvalidAccessToken);

		let url = Api.getBaseUrl()
			+ 'v1/content-code'
			+ '?content-id=' + contentId;

		let init = {
			method: 'GET',
			headers: {
				'Authorization': 'Bearer ' + session.accessToken
			}
		};

		return fetch(url, init).then((response: Response) => {
			if (response.status === 200)
				return response.json();
			else
				throw new Error("Request failed, status: " + response.status);

		}).then((jsonObject: any) => {
			console.log(jsonObject);

			if (typeof jsonObject === 'object' &&
				Array.isArray(jsonObject['items'])) {
				let contents = new Array<ContentCode>();

				for (let item of jsonObject['items']) {
					let content = ContentCode.fromJson(item);

					if (content !== undefined)
						contents.push(content);
				}

				return contents;
			}
			else {
				throw new Error("Parsing content data failed");
			}
		}).catch(reason => Promise.reject(reason));
	}

	public async createContentCode(contentCode: ContentCode): Promise<number> {
		const session = this.loginService.getSession();

		if (session === undefined)
			return Promise.reject("No session");

		if (typeof session.accessToken !== 'string')
			return Promise.reject(ApiError.InvalidAccessToken);

		let url = Api.getBaseUrl() + 'v1/content-code';

		let init = {
			method: 'POST',
			headers: {
				'Authorization': 'Bearer ' + session.accessToken
			},
			body: JSON.stringify(contentCode)
		};

		let result = await fetch(url, init);

		return result.status;
	}

	public async editContentCode(data: ContentCodeEditData): Promise<number> {
		const session = this.loginService.getSession();

		if (session === undefined)
			return Promise.reject("No session");

		if (typeof session.accessToken !== 'string')
			return Promise.reject(ApiError.InvalidAccessToken);

		let url = Api.getBaseUrl() + 'v1/content-code';

		let init = {
			method: 'PUT',
			headers: {
				'Authorization': 'Bearer ' + session.accessToken
			},
			body: JSON.stringify(data)
		};

		console.log(init.body);

		let result = await fetch(url, init);
		return result.status;
	}

	public async getContentCodeActivations(code: string): Promise<ContentCodeActivation[]> {
		const session = this.loginService.getSession();

		if (session === undefined)
			return Promise.reject("No session");

		if (typeof session.accessToken !== 'string')
			return Promise.reject(ApiError.InvalidAccessToken);

		let url = Api.getBaseUrl()
			+ 'v1/content-code-activation/'
			+ code;

		let init = {
			method: 'GET',
			headers: {
				'Authorization': 'Bearer ' + session.accessToken
			}
		};

		console.log("GET");

		return fetch(url, init).then((response: Response) => {
			if (response.status === 200)
				return response.json();
			else
				throw new Error("Request failed, status: " + response.status);

		}).then((jsonObject: any) => {
			console.log("OK");
			console.log(jsonObject);

			if (Array.isArray(jsonObject)) {
				let contents = new Array<ContentCodeActivation>();

				for (let item of jsonObject) {
					let content = ContentCodeActivation.fromJson(item);

					if (content !== undefined)
						contents.push(content);
				}

				return contents;
			}
			else {
				throw new Error("Parsing content activation data failed");
			}
		}).catch(reason => Promise.reject(reason));
	}

	public async enableCodeActivation(code: string, id: string): Promise<boolean> {
		const session = this.loginService.getSession();

		if (session === undefined)
			return Promise.reject("No session");

		if (typeof session.accessToken !== 'string')
			return Promise.reject(ApiError.InvalidAccessToken);

		let url = Api.getBaseUrl() + 'v1/content-code-activation/' + code + "?id=" + id;

		let init = {
			method: 'POST',
			headers: {
				'Authorization': 'Bearer ' + session.accessToken
			}
		};

		return fetch(url, init)
			.then((response: Response) => {
				if (response.status === 200)
					return true;
				else
					return false;

			})
			.catch(reason => Promise.reject(reason));
	}

	public async disableCodeActivation(code: string, id: string): Promise<boolean> {
		const session = this.loginService.getSession();

		if (session === undefined)
			return Promise.reject("No session");

		if (typeof session.accessToken !== 'string')
			return Promise.reject(ApiError.InvalidAccessToken);

		let url = Api.getBaseUrl() + 'v1/content-code-activation/' + code + "?id=" + id;

		let init = {
			method: 'DELETE',
			headers: {
				'Authorization': 'Bearer ' + session.accessToken
			}
		};

		return fetch(url, init)
			.then((response: Response) => {
				if (response.status === 200)
					return true;
				else
					return false;

			})
			.catch(reason => Promise.reject(reason));
	}

	public async getContentSets(contentId: string): Promise<Array<PTProgram>> {
		const session = this.loginService.getSession();

		if (session === undefined)
			return Promise.reject("No session");

		if (typeof session.accessToken !== 'string')
			return Promise.reject(ApiError.InvalidAccessToken);

		let url = Api.getBaseUrl()
			+ 'v1/content/'
			+ contentId + '/'
			+ 'sets';

		let init = {
			method: 'GET',
			headers: {
				'Authorization': 'Bearer ' + session.accessToken
			}
		};

		return fetch(url, init).then((response: Response) => {
			if (response.status === 200)
				return response.json();
			else
				throw new Error("Request failed, status: " + response.status);
		})
			.then((jsonObject: any) => {
				console.log(jsonObject);

				if (Array.isArray(jsonObject)) {
					let programs = new Array<PTProgram>();

					for (let item of jsonObject) {
						let prog = PTProgram.fromJson(item);

						if (prog !== undefined)
							programs.push(prog);
					}

					return programs;
				}
				else {
					throw new Error("Patsing content programs failed");
				}
			})
			.catch(Promise.reject);
	}
}